import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo, useRef } from "react";
import { Music, Radio, UserPlus, MessageSquare, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { MessageNotificationProvider, useMessageNotifications } from "@/hooks/use-message-notifications";
import UnifiedParticipantsTable from "@/components/unified-participants-table";
import UnifiedOnStageTable from "@/components/unified-on-stage-table";
import SelectedForStageTable from "@/components/selected-for-stage-table";
import ConsiderationTable from "@/components/consideration-table";
import AddParticipantModal from "@/components/add-participant-modal";
import JamMessaging from "@/components/jam-messaging";
import RaffleModal from "@/components/raffle-modal";
import { PageBackground } from "@/components/ui/page-background";
import { TrackerHeader } from "@/components/tracker-header";
import { FocusedViewSelector } from "@/components/focused-view-selector";
import type { Performer, JamNight } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import SetTimeLimitModal from "@/components/set-time-limit-modal";
import { useStablePerformers } from "@/hooks/use-stable-performers";
import { useLocation, useRoute } from "wouter";
import { KanbanView } from "@/components/kanban-view";
import { ManageTab } from "@/components/manage-tab";

interface JamTrackerProps {
  jamNightId: number;
  activeTab?: string;
}


export default function JamTracker(props: JamTrackerProps) {
  return (
    <MessageNotificationProvider jamNightId={props.jamNightId}>
      <JamTrackerContent {...props} />
    </MessageNotificationProvider>
  );
}

function JamTrackerContent({ jamNightId, activeTab: urlActiveTab }: JamTrackerProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTimeLimitModal, setShowTimeLimitModal] = useState(false);
  const [showRaffleModal, setShowRaffleModal] = useState(false);
  const [showFocusedViewSelector, setShowFocusedViewSelector] = useState(false);
  
  // Load kanban settings from localStorage
  const [isKanbanView, setIsKanbanView] = useState(() => {
    const saved = localStorage.getItem('jam-tracker-kanban-view');
    return saved === 'true';
  });
  const [showParticipantsInKanban, setShowParticipantsInKanban] = useState(() => {
    const saved = localStorage.getItem('jam-tracker-show-participants');
    return saved === 'true';
  });
  const [showConsiderationInKanban, setShowConsiderationInKanban] = useState(() => {
    const saved = localStorage.getItem('jam-tracker-show-consideration-kanban');
    return saved !== 'false'; // Default to true
  });
  const [showConsiderationInNormalView, setShowConsiderationInNormalView] = useState(() => {
    const saved = localStorage.getItem('jam-tracker-show-consideration-normal');
    return saved !== 'false'; // Default to true
  });
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const messagingPanelRef = useRef<HTMLDivElement>(null);
  // @ts-ignore
  const { hasNewMessages, setIsMessagingOpen, currentTab, setCurrentTab } = useMessageNotifications();

  // Use currentTab from context as the source of truth
  const selectedTab = currentTab;

  // Sync tab changes with context and URL
  const handleTabChange = (value: string) => {
    setCurrentTab(value);
    if (value === "chat") {
      setIsMessagingOpen(true);
    }
    // Update URL when tab changes
    setLocation(`/event/${jamNightId}/${value}`);
  };

  // Save kanban view preference
  useEffect(() => {
    localStorage.setItem('jam-tracker-kanban-view', isKanbanView.toString());
  }, [isKanbanView]);

  // Save participants visibility preference
  useEffect(() => {
    localStorage.setItem('jam-tracker-show-participants', showParticipantsInKanban.toString());
  }, [showParticipantsInKanban]);

  // Save consideration visibility preference for kanban
  useEffect(() => {
    localStorage.setItem('jam-tracker-show-consideration-kanban', showConsiderationInKanban.toString());
  }, [showConsiderationInKanban]);

  // Save consideration visibility preference for normal view
  useEffect(() => {
    localStorage.setItem('jam-tracker-show-consideration-normal', showConsiderationInNormalView.toString());
  }, [showConsiderationInNormalView]);

  // Initialize tab from URL or use default
  useEffect(() => {
    if (urlActiveTab) {
      // Valid tabs for jam tracker
      const validTabs = ["live", "signups", "chat", "manage"];
      if (validTabs.includes(urlActiveTab)) {
        setCurrentTab(urlActiveTab);
        if (urlActiveTab === "chat") {
          setIsMessagingOpen(true);
        }
      } else {
        // Invalid tab in URL, replace URL without creating history entry
        window.history.replaceState(null, '', `/event/${jamNightId}/signups`);
        setCurrentTab("signups");
      }
    } else {
      // No tab in URL, replace URL without creating history entry
      window.history.replaceState(null, '', `/event/${jamNightId}/signups`);
      setCurrentTab("signups");
    }
  }, [urlActiveTab, jamNightId, setCurrentTab, setIsMessagingOpen]);

  const showMultiScreenLayout = user?.multiScreenJams;

  const { data: jamNight, isLoading: jamNightLoading } = useQuery<JamNight>({
    queryKey: [`/api/events/${jamNightId}`],
  });

  const { data: rawPerformers = [], isLoading: performersLoading } = useQuery<
    Performer[]
  >({
    queryKey: [`/api/performers?jamNightId=${jamNightId}`],
  });

  // Stabilize performers array to prevent unnecessary re-renders of child components
  const performers = useStablePerformers(rawPerformers);

  const resetAllPerformersMutation = useMutation({
    mutationFn: async () => {
      // Sort performers alphabetically by name
      const sortedPerformers = [...performers].sort((a, b) => 
        a.name.localeCompare(b.name)
      );

      // Create reset promises with new sort orders
      const resetPromises = sortedPerformers.map((performer, index) =>
        apiRequest("PATCH", `/api/performers/${performer.id}`, {
          played: false,
          timeOnStage: 0,
          status: 'participant',
          stageStartTime: null,
          currentInstruments: [],
          replacementName: null,
          replacingPerformerId: null,
          performanceCount: 0, // Reset performance count
          sortOrder: (index + 1) * 1000, // Assign sort orders with gaps of 1000
        })
      );
      await Promise.all(resetPromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
      toast({
        title: "Success",
        description: "All participants have been reset successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to reset participants. Please try again.",
        variant: "destructive",
      });
    },
  });

  const clearOffStagePerformersMutation = useMutation({
    mutationFn: async () => {
      const onStagePerformers = performers.filter(performer => 
        performer.status === 'on_stage' || performer.status === 'pending_removal'
      );
      const updatePromises = onStagePerformers.map(performer =>
        apiRequest("PATCH", `/api/performers/${performer.id}`, {
          status: 'participant',
          stageStartTime: null,
          currentInstruments: [],
          replacementName: null,
        })
      );
      
      // Also clear replacement targeting data from any performers targeting the cleared performers
      const clearReplacementPromises = performers
        .filter(p => onStagePerformers.some(onStagePerformer => onStagePerformer.id === p.replacingPerformerId))
        .map(performer =>
          apiRequest("PATCH", `/api/performers/${performer.id}`, {
            replacingPerformerId: null,
          })
        );
      
      await Promise.all([...updatePromises, ...clearReplacementPromises]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
      toast({
        title: "Success",
        description: "All performers have been taken off stage successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to clear stage. Please try again.",
        variant: "destructive",
      });
    },
  });

  const userRole = user?.role === 'god' 
    ? 'god' 
    : user?.organisationUsers?.find(
        (ou) => ou.organisation.id === jamNight?.organisationId
      )?.role;

  const isLoading = jamNightLoading || performersLoading;
  const isEventCompleted = jamNight?.completed || false;
  
  // Check if there are people on stage for live indicator
  const hasPerformersOnStage = useMemo(() => 
    performers.some(p => (p.status === 'on_stage' || p.status === 'pending_removal') && !p.removed), [performers]);


  if (isLoading) {
    return (
      <PageBackground className="flex items-center justify-center">
        <div className="text-center relative z-10">
          <Music className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700 drop-shadow-sm" />
          <p className="text-slate-700 drop-shadow-sm">Loading jam night data...</p>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      
      <TrackerHeader
        jamNightId={jamNightId}
        isEventCompleted={isEventCompleted}
        onAddParticipant={() => setShowAddModal(true)}
        showMessageNotification={true}
        onMessageNotificationClick={() => {
          if (user?.multiScreenJams) {
            setCurrentTab("chat");
          } else {
            setIsMessagingOpen(true);
            setTimeout(() => {
              messagingPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
          }
        }}
        hasNewMessages={hasNewMessages}
        showKanbanControls={selectedTab === "live"}
        isKanbanView={isKanbanView}
        onToggleKanbanView={() => setIsKanbanView(!isKanbanView)}
        showParticipantsInKanban={showParticipantsInKanban}
        onToggleParticipants={() => setShowParticipantsInKanban(!showParticipantsInKanban)}
        showConsiderationInKanban={showConsiderationInKanban}
        onToggleConsideration={() => setShowConsiderationInKanban(!showConsiderationInKanban)}
        onFocusedViewClick={() => setShowFocusedViewSelector(true)}
      />

      <main className="mx-auto px-4 sm:px-6 lg:px-32 xl:px-48 pt-4 pb-6 relative z-10">
        {!showMultiScreenLayout && <div ref={messagingPanelRef}><JamMessaging jamNightId={jamNightId} disabled={isEventCompleted} /></div>}

        {showMultiScreenLayout ? (
            <Tabs value={selectedTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-white/30 backdrop-blur-md border border-white/20 shadow-lg p-1">
                <TabsTrigger 
                  value="live"
                  className="data-[state=active]:bg-white/40 data-[state=active]:text-blue-600 data-[state=active]:font-semibold data-[state=active]:shadow-md transition-all duration-300 flex items-center gap-2"
                >
                  <Radio className={`h-4 w-4 ${hasPerformersOnStage ? 'text-green-500 animate-pulse' : ''}`} />
                  Live
                </TabsTrigger>
                <TabsTrigger 
                  value="signups"
                  className="data-[state=active]:bg-white/40 data-[state=active]:text-blue-600 data-[state=active]:font-semibold data-[state=active]:shadow-md transition-all duration-300 flex items-center gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  List
                </TabsTrigger>
                <TabsTrigger 
                  value="chat"
                  className="data-[state=active]:bg-white/40 data-[state=active]:text-blue-600 data-[state=active]:font-semibold data-[state=active]:shadow-md transition-all duration-300 flex items-center gap-2"
                >
                  {hasNewMessages && selectedTab !== "chat" ? (
                    <Badge
                      variant="destructive"
                      className="h-4 w-4 rounded-full p-0 flex items-center justify-center text-xs bg-red-500/80 backdrop-blur-sm border border-red-400/30"
                    >
                      !
                    </Badge>
                  ) : (
                    <MessageSquare className="h-4 w-4" />
                  )}
                  Chat
                </TabsTrigger>
                <TabsTrigger 
                  value="manage"
                  className="data-[state=active]:bg-white/40 data-[state=active]:text-blue-600 data-[state=active]:font-semibold data-[state=active]:shadow-md transition-all duration-300 flex items-center gap-2"
                >
                  <Settings className="h-4 w-4" />
                  Manage
                </TabsTrigger>
              </TabsList>

              <TabsContent value="live" className="mt-4 space-y-4">
                {/* Use standard view on mobile (below lg breakpoint), kanban view only on desktop */}
                <div className="block lg:hidden">
                  {/* Mobile - Always show standard view */}
                  {(() => {
                    const onStagePerformers = performers.filter(p => (p.status === 'on_stage' || p.status === 'pending_removal') && !p.removed);
                    const selectedForStagePerformers = performers.filter(p => p.status === 'selected' && !p.removed);
                    const hasLiveContent = onStagePerformers.length > 0 || selectedForStagePerformers.length > 0;

                    if (!hasLiveContent) {
                      return (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                          <Music className="h-12 w-12 text-slate-400 mb-4" />
                          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Performers On Stage</h3>
                          <p className="text-slate-600 max-w-md">
                            Select participants from the List tab to move them on stage or prepare them for staging.
                          </p>
                        </div>
                      );
                    }

                    return (
                      <>
                        {jamNight && <UnifiedOnStageTable performers={performers} jamNightId={jamNightId} jamNight={jamNight} disabled={isEventCompleted} mode="jam-night" />}
                        <SelectedForStageTable performers={performers} jamNightId={jamNightId} disabled={isEventCompleted} />
                        {showConsiderationInNormalView && <ConsiderationTable performers={performers} jamNightId={jamNightId} disabled={isEventCompleted} />}
                      </>
                    );
                  })()}
                </div>
                
                <div className="hidden lg:block">
                  {/* Desktop - Respect kanban view setting */}
                  {isKanbanView ? (
                    <KanbanView
                      performers={performers}
                      jamNightId={jamNightId}
                      jamNight={jamNight}
                      isEventCompleted={isEventCompleted}
                      showParticipantsInKanban={showParticipantsInKanban}
                      showConsiderationInKanban={showConsiderationInKanban}
                    />
                  ) : (
                    /* Standard View */
                    (() => {
                      const onStagePerformers = performers.filter(p => (p.status === 'on_stage' || p.status === 'pending_removal') && !p.removed);
                      const selectedForStagePerformers = performers.filter(p => p.status === 'selected' && !p.removed);
                      const hasLiveContent = onStagePerformers.length > 0 || selectedForStagePerformers.length > 0;

                      if (!hasLiveContent) {
                        return (
                          <div className="flex flex-col items-center justify-center py-16 text-center">
                            <Music className="h-12 w-12 text-slate-400 mb-4" />
                            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Performers On Stage</h3>
                            <p className="text-slate-600 max-w-md">
                              Select participants from the List tab to move them on stage or prepare them for staging.
                            </p>
                          </div>
                        );
                      }

                      return (
                        <>
                          {jamNight && <UnifiedOnStageTable performers={performers} jamNightId={jamNightId} jamNight={jamNight} disabled={isEventCompleted} mode="jam-night" />}
                          <SelectedForStageTable performers={performers} jamNightId={jamNightId} disabled={isEventCompleted} />
                          {showConsiderationInNormalView && <ConsiderationTable performers={performers} jamNightId={jamNightId} disabled={isEventCompleted} />}
                        </>
                      );
                    })()
                  )}
                </div>
              </TabsContent>

              <TabsContent value="signups" className="mt-4 space-y-4">
                <UnifiedParticipantsTable performers={performers} jamNightId={jamNightId} jamNight={jamNight} disabled={isEventCompleted} mode="jam-night" />
              </TabsContent>

              <TabsContent value="manage" className="mt-4 space-y-4">
                <ManageTab
                  jamNight={jamNight}
                  jamNightId={jamNightId}
                  performers={performers}
                  isEventCompleted={isEventCompleted}
                  userRole={userRole}
                  showConsiderationInNormalView={showConsiderationInNormalView}
                  setShowConsiderationInNormalView={setShowConsiderationInNormalView}
                  clearOffStagePerformersMutation={clearOffStagePerformersMutation}
                  resetAllPerformersMutation={resetAllPerformersMutation}
                  setShowTimeLimitModal={setShowTimeLimitModal}
                  setShowRaffleModal={setShowRaffleModal}
                />
              </TabsContent>

              <TabsContent value="chat" className="mt-4 space-y-4">
                <JamMessaging jamNightId={jamNightId} disabled={isEventCompleted} forceOpen={selectedTab === "chat"} />
              </TabsContent>
            </Tabs>
        ) : (
            <div className="space-y-4">
              {jamNight && <UnifiedOnStageTable performers={performers} jamNightId={jamNightId} jamNight={jamNight} disabled={isEventCompleted} mode="jam-night" />}
              <SelectedForStageTable performers={performers} jamNightId={jamNightId} disabled={isEventCompleted} />
              {showConsiderationInNormalView && <ConsiderationTable performers={performers} jamNightId={jamNightId} disabled={isEventCompleted} />}
              <UnifiedParticipantsTable performers={performers} jamNightId={jamNightId} jamNight={jamNight} disabled={isEventCompleted} mode="jam-night" />
              <ManageTab
                jamNight={jamNight}
                jamNightId={jamNightId}
                performers={performers}
                isEventCompleted={isEventCompleted}
                userRole={userRole}
                showConsiderationInNormalView={showConsiderationInNormalView}
                setShowConsiderationInNormalView={setShowConsiderationInNormalView}
                clearOffStagePerformersMutation={clearOffStagePerformersMutation}
                resetAllPerformersMutation={resetAllPerformersMutation}
                setShowTimeLimitModal={setShowTimeLimitModal}
                setShowRaffleModal={setShowRaffleModal}
              />
            </div>
        )}
      </main>

      {jamNight && (
        <SetTimeLimitModal
          isOpen={showTimeLimitModal}
          onOpenChange={setShowTimeLimitModal}
          jamNight={jamNight}
        />
      )}

      <AddParticipantModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        jamNightId={jamNightId}
      />

      <RaffleModal
        open={showRaffleModal}
        onOpenChange={setShowRaffleModal}
        performers={performers.filter((p) => !p.removed)}
        jamNightId={jamNightId}
      />

      <FocusedViewSelector
        open={showFocusedViewSelector}
        onOpenChange={setShowFocusedViewSelector}
        jamNightId={jamNightId}
      />
    </PageBackground>
  );
}